import { Registry, withRegistry } from '@bem-react/di';
// @ts-ignore
import { ANSWER_STATUSES } from '@yandex-int/k-common/constants';
import b from 'b_';
import cx from 'classnames';
import React, { ReactElement, useCallback, useContext, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useMountedState } from 'react-use';

import { getSyntaxError } from '../__error/useSyntaxError';
import useMisclick from '../../../utils/hooks/useMisclick';
import { useIntl } from '../../intl/context';
import { Tooltip } from '../../LegoTooltip/LegoTooltip';
import PaginatorButtonBase, { PaginatorButtonProps } from '../../paginator/__button/paginator__button';
import { Paginator as PaginatorBase } from '../../paginator/paginator';
import { PAGINATOR_BUTTON_VIEW, PAGINATOR_ORIENTATION, PAGINATOR_THEME } from '../../paginator/paginator.constants';
import { cnPaginator } from '../../paginator/utils';
import { getShouldShowCurrentAnswersStatus } from '../../problem/common-utils';
import { switchContext, SwitchContextProvider, useRobotSelector } from '../context';
import { ROBOT_RUN_STATUS } from '../robot.constants';
import { RobotSample } from '../robot.types';

import './robot__sample-switch.scss';

interface SampleSwitchProps {
    samples: RobotSample[];
    sampleStatuses?: (ANSWER_STATUSES | undefined)[];
    title?: ReactElement;
    selected?: number;
    setSelected: (selected: number) => void;
}

const MAXIMUM_CAPACITY = 8;
const BUTTON_CAPACITY = 2;
const ROBOT_SWITCH_CLASSNAME = 'robot__sample-switch';

const ruToTankerKey: Record<string, string> = {
    'Задача решена успешно': '{{robot.sampleSwitch.verdict.correct}}',
    'Задача завершилась с ошибкой': '{{robot.sampleSwitch.verdict.error}}',
    'Задача решена с ошибкой': '{{robot.sampleSwitch.verdict.failured}}',
    'Ход невозможен, справа стена': '{{robot.sampleSwitch.verdict.wall_from_right}}',
    'Ход невозможен, слева стена': '{{robot.sampleSwitch.verdict.wall_from_left}}',
    'Ход невозможен, сверху стена': '{{robot.sampleSwitch.verdict.wall_from_top}}',
    'Ход невозможен, снизу стена': '{{robot.sampleSwitch.verdict.wall_from_bottom}}',
};

const PaginatorButton = ({ mix, ...props }: PaginatorButtonProps) => {
    const intl = useIntl();
    const buttonRef = useRef<HTMLButtonElement>(null);
    const isFinished = useRobotSelector((s) => s.state?.status === ROBOT_RUN_STATUS.FINISH);
    const attempt = useRobotSelector((s) => s.attempt);
    const results = useRobotSelector((s) => s.state?.results);
    const resultsSample = results?.result?.[props.number];
    const attemptSample = attempt?.markers?.result?.[props.number];
    let msg = (isFinished && resultsSample?.check_details.verdict?.msg) || attemptSample?.check_details.verdict?.msg;

    if (msg) {
        if (ruToTankerKey[msg]) {
            msg = ruToTankerKey[msg];
        }

        msg = msg.replace(/{{([a-zA-Z]+\..*)}}/g, (_: unknown, verdictTankerKey: string) => {
            return intl.formatMessage({ id: verdictTankerKey });
        });

        if (msg.includes('Traceback') || msg.includes('SyntaxError')) {
            msg = intl.formatMessage({ id: 'robot.syntaxError' });
        }
    }

    const shouldRenderTooltip = msg && props.view === PAGINATOR_BUTTON_VIEW.ORANGE;

    const { onSampleEnter, onSampleLeave, tooltipSample } = useContext(switchContext);
    const tooltipScope = useRobotSelector((s) => s.fieldBgRef);

    return (
        <>
            {shouldRenderTooltip && (
                <Tooltip
                    anchor={buttonRef}
                    className={cx(b('robot', 'sample-switch-tooltip'))}
                    direction="bottom"
                    hasTail
                    key={msg}
                    mainOffset={16}
                    scope={tooltipScope}
                    view="default"
                    visible={tooltipSample === props.number}
                >
                    {msg}
                </Tooltip>
            )}
            <PaginatorButtonBase
                {...props}
                mix={cx(
                    mix,
                    b('robot', 'sample-switch-button', {
                        view: props.view,
                        selected: props.selected,
                        clickable: props.clickable,
                    })
                )}
                onMouseEnter={() => onSampleEnter(props.number)}
                onMouseOut={() => onSampleLeave()}
                ref={buttonRef}
            />
        </>
    );
};

const registry = new Registry({ id: cnPaginator() });
registry.set('PaginatorButton', PaginatorButton);
const Paginator = withRegistry(registry)(PaginatorBase);

const getView = (status?: ANSWER_STATUSES, hidden?: boolean): PAGINATOR_BUTTON_VIEW => {
    if (hidden) {
        return PAGINATOR_BUTTON_VIEW.EXTRA_DARK;
    }
    if (status === ANSWER_STATUSES.CORRECT) {
        return PAGINATOR_BUTTON_VIEW.GREEN;
    }
    if (status === ANSWER_STATUSES.INCORRECT) {
        return PAGINATOR_BUTTON_VIEW.ORANGE;
    }
    return PAGINATOR_BUTTON_VIEW.DEFAULT;
};

export const SampleSwitch = (props: SampleSwitchProps) => {
    const { samples, sampleStatuses, title, selected, setSelected } = props;

    const pages = useMemo(
        () =>
            samples.map((sample, index) => ({
                onClick: () => setSelected(index),
                selected: selected === index,
                view: getView(sampleStatuses?.[index], sample.hidden),
                number: index,
                text: (index + 1).toString(),
                disabled: sample.hidden,
            })),
        [samples, selected, sampleStatuses, setSelected]
    );

    const showButtons = samples.length > MAXIMUM_CAPACITY;

    return (
        <div className={ROBOT_SWITCH_CLASSNAME}>
            {title && <div className="robot__sample-switch-title">{title}</div>}
            <Paginator
                aria-label={undefined}
                capacity={showButtons ? MAXIMUM_CAPACITY - BUTTON_CAPACITY : MAXIMUM_CAPACITY}
                orientation={PAGINATOR_ORIENTATION.HORIZONTAL}
                pages={pages}
                showDownButton={showButtons}
                showUpButton={showButtons}
                theme={PAGINATOR_THEME.CLEAR}
            />
        </div>
    );
};

const RobotSampleSwitch = () => {
    const playResults = useRobotSelector((s) => s.state.results);
    const attempt = useRobotSelector((s) => s.attempt);
    const isFinished = useRobotSelector((s) => s.state?.status === ROBOT_RUN_STATUS.FINISH);
    const showCurrentAnswersStatus = useRobotSelector(getShouldShowCurrentAnswersStatus);
    const attemptResults = attempt?.markers;
    const showCorrectAnswers = useRobotSelector((s) => s.showCorrectAnswers);

    const props = useRobotSelector((s) => ({
        samples: s.samples,
        selected: s.sample,
        setSelected: s.setSample,
    }));

    const selectedRef = useRef(props.selected);
    selectedRef.current = props.selected;

    const sampleStatuses = useMemo(() => {
        // Если воспроизводим новый код или не показываем ошибки, то красим только
        // текущий тест после завершения воспроизведения
        const runNewCode = playResults && playResults !== attemptResults;
        if (runNewCode || !showCurrentAnswersStatus) {
            return Object.entries(playResults?.result || {}).map(([, sample], i) => {
                const status = sample.answer_status ? ANSWER_STATUSES.CORRECT : ANSWER_STATUSES.INCORRECT;
                return i === selectedRef.current && isFinished ? status : undefined;
            });
        }
        // Не красим тесты в случае синтаксической ошибки
        if (getSyntaxError(attemptResults)) {
            return [];
        }
        return Object.values(attemptResults?.result || {}).map((sample) => {
            return sample.answer_status || showCorrectAnswers ? ANSWER_STATUSES.CORRECT : ANSWER_STATUSES.INCORRECT;
        });
    }, [attemptResults, isFinished, playResults, showCorrectAnswers, showCurrentAnswersStatus]);

    const [tooltipSample, setTooltipSample] = useState<number | null>(null);

    const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
    const clearTooltipTimeout = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
    }, []);

    const value = useMemo(
        () => ({
            tooltipSample,
            onSampleEnter: (s: number) => {
                setTooltipSample(s);
                clearTooltipTimeout();
            },
            onSampleLeave: () => {
                setTooltipSample(null);
                clearTooltipTimeout();
            },
        }),
        [clearTooltipTimeout, tooltipSample]
    );

    const showTimeRef = useRef<number>();
    const isMounted = useMountedState();

    // Показ тултипа какое-то время
    const showTooltipForSomeTime = useCallback(
        (sample: number) => {
            setTooltipSample(sample);
            clearTooltipTimeout();
            showTimeRef.current = Date.now();
            timeoutRef.current = setTimeout(() => {
                if (isMounted()) {
                    setTooltipSample(null);
                }
            }, 7000);
        },
        [clearTooltipTimeout, isMounted]
    );

    const setSelected = props.setSelected;

    // После изменения статусов (отправки ответа или выключения показа решения)
    //  переход на первый неправильно решенный тест и показ его тултипа
    useLayoutEffect(() => {
        if (playResults || !showCurrentAnswersStatus) {
            return;
        }
        const firstIncorrect = sampleStatuses.findIndex((st) => ANSWER_STATUSES.INCORRECT === st);
        if (firstIncorrect !== -1) {
            setSelected(firstIncorrect);
            showTooltipForSomeTime(firstIncorrect);
        }
    }, [playResults, sampleStatuses, setSelected, showCurrentAnswersStatus, showTooltipForSomeTime]);

    // Показ тултипа по завершению воспроизведения кода
    useLayoutEffect(() => {
        if (isFinished) {
            showTooltipForSomeTime(props.selected);
        }
    }, [isFinished, props.selected, showTooltipForSomeTime]);

    // Скрытие тултипа по нажатию на любой место кроме пагинатора тестов
    const handleMisclick = useCallback(() => {
        if (showTimeRef.current) {
            // Не обрабатываем мисклик 500 мс после автоматического показа тултипа
            if (Date.now() > showTimeRef.current + 500) {
                value.onSampleLeave();
            }
        } else {
            value.onSampleLeave();
        }
    }, [value]);
    useMisclick(handleMisclick, `.${ROBOT_SWITCH_CLASSNAME}`);

    return (
        <SwitchContextProvider value={value}>
            <SampleSwitch sampleStatuses={sampleStatuses} {...props} />
        </SwitchContextProvider>
    );
};

export default RobotSampleSwitch;
